import { Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { combineLatest } from "rxjs";

import { AppConfig } from "src/app/app.config";
import { Features } from "src/app/models/features";
import { FeatureFlags } from "src/app/models/feature-flags";
import { AuthFacade } from "src/app/store/auth/facade/auth.facade";
import { FeatureFlagsFacade } from "src/app/store/feature-flags/facade/feature-flags.facade";
import { TokenService } from "src/app/services/token/token.service";

import rg4js from "raygun4js";

interface RaygunUserData {
    identifier: string;
    email?: string;
    firstName?: string;
    fullName?: string;
}

@Injectable()
export class RaygunService {
    private raygunApiKey: string;

    constructor(
        private appConfig: AppConfig,
        private authFacade: AuthFacade,
        private featureFlagsFacade: FeatureFlagsFacade,
        private tokenService: TokenService,
    ) {
        // Get the Raygun API key, this is congifured in Octopus
        this.raygunApiKey = this.appConfig.getFeatureProperty(Features.RAYGUN, "raygunApiKey");

        if (this.raygunApiKey) {
            this.initializeRaygun();
            this.setRaygunPropertiesSubscription();
        } else {
            console.log("Raygun API key not found, Raygun will not be initialized");
        }
    }

    private initializeRaygun() {
        rg4js("apiKey", this.raygunApiKey);
        rg4js("enableRealUserMonitoring", true);

        // Enable crash reporting
        rg4js("enableCrashReporting", true);

        // Set up the library to transmit data for the currently logged-in user
        rg4js("options", {
            // User-aborted Ajax calls result in errors - if this option is true, these will not be sent
            ignoreAjaxAbort: true,
            // Ajax requests that return error codes will not be sent as errors to Raygun if this option is true
            ignoreAjaxError: true,
            // ignores any errors that have no stack trace information. This will discard any errors that occur
            // completely within 3rd party scripts - if code loaded from the current domain called the 3rd party
            // function, it will have at least one stack line and will still be sent
            ignore3rdPartyErrors: true,
            // Prevent user data being sent for anonymous users (those where setUser hasn't been called)
            disableAnonymousUserTracking: true
        });

        // Sensitive data
        rg4js("filterSensitiveData", ["password", "bankAccount", "phoneNumber", "passportNumber"]);

        // Adding tags
        var appName = AppConfig.settings.env.name;
        rg4js("withTags", ["Tongariro", appName]);

        rg4js('boot');
    }

    private setRaygunPropertiesSubscription() {
        combineLatest([
            this.authFacade.userLoggedIn$(),
            this.featureFlagsFacade.isFeatureFlagEnabled$(FeatureFlags.EnableRealUserMonitoring),
            this.tokenService.claimsNotNull$,
        ])
            .pipe(takeUntilDestroyed())
            .subscribe(([isLoggedIn, isRealUserMonitoringEnabled, claims]: [boolean, boolean, any]) => {
                if (isLoggedIn && isRealUserMonitoringEnabled) {
                    let userData = this.getUserData(claims);

                    if (userData.identifier != null) {
                        this.setRaygunUser(userData);
                    }
                } else {
                    this.resetRaygunUser();
                }
            });
    }

    private getUserData(claims: any): RaygunUserData {
        let userData: RaygunUserData = {
            identifier: claims["primarysid"],
            email: claims["unique_name"],
            fullName: claims["given_name"],
        };

        return userData;
    }

    private setRaygunUser(user: RaygunUserData) {
        // Set the user data for all subsequent crash reports
        rg4js("setUser", {
            identifier: user.identifier,
            isAnonymous: false,
            email: user.email,
            fullName: user.fullName,
            firstName: user.firstName,
        });
    }

    private resetRaygunUser() {
        // Reset the user data for all subsequent crash reports
        rg4js("setUser", {
            identifier: "",
            isAnonymous: true,
            email: "",
            fullName: "",
            firstName: "",
        });
    }
}
